import classnames from 'classnames'
import { useField } from 'formik'
import * as React from 'react'
import { getCounterSuffix, type TranslatePairFn } from 'src/helpers/fns'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useFormData } from './Form'

interface Props {
  readonly className?: string
  readonly placeholder?: string
  readonly name: string
  readonly required?: boolean
  readonly disabled?: boolean
  readonly label?: string
  readonly rows?: number
  readonly cols?: number
  readonly labelAnimation?: boolean
  readonly instructions?: React.ReactElement
  readonly minLength?: number
  readonly maxLength?: number
  readonly countMode?: 'characters' | 'words'
}

function countWords(text: string | undefined | null): number {
  if (!text || text.trim() === '') return 0
  return text.trim().split(/\s+/).length
}

function getCount(value: string | undefined | null, isWordMode: boolean): number {
  return isWordMode ? countWords(value) : (value?.length ?? 0)
}

export default function TextareaInput({
  required = false,
  labelAnimation = false,
  instructions,
  minLength,
  maxLength,
  countMode = 'characters',
  className,
  label,
  name,
  ...restProps
}: Props): React.ReactElement {
  const t = useTranslatable()
  const isWordMode = countMode === 'words'
  const id = React.useId()

  // Memoize validation function to avoid stale closures
  const validate = React.useCallback(
    (value: string): string | undefined => {
      if (required && !value) {
        return label != null ? `${label} ${t('common:is_required')}` : t('common:field_is_required')
      }

      const count = getCount(value, isWordMode)

      if (minLength != null && count < minLength) {
        return isWordMode
          ? t([`მინიმუმ ${minLength} სიტყვა`, `Minimum ${minLength} words`])
          : t([`მინიმუმ ${minLength} სიმბოლო`, `Minimum ${minLength} characters`])
      }

      if (maxLength != null && count > maxLength) {
        return isWordMode
          ? t([`მაქსიმუმ ${maxLength} სიტყვა`, `Maximum ${maxLength} words`])
          : t([`მაქსიმუმ ${maxLength} სიმბოლო`, `Maximum ${maxLength} characters`])
      }

      return undefined
    },
    [required, label, t, isWordMode, minLength, maxLength]
  )

  const [field, meta] = useField({ name, validate })
  const [formData] = useFormData()

  const currentCount = React.useMemo(() => getCount(field.value, isWordMode), [field.value, isWordMode])
  const errors = formData.errors.fields[name] ?? []
  const hasErrors = (errors.length > 0 || meta.error != null) && meta.touched
  const hasValue = Boolean(field.value)
  const showCounter = minLength != null || maxLength != null
  const isOverLimit = maxLength != null && currentCount > maxLength
  const counterSuffix = getCounterSuffix({ minLength, maxLength, isWordMode, t: t as TranslatePairFn })

  return (
    <div className="relative flex w-full flex-col overflow-x-hidden">
      <div className="w-full">
        <div
          className={classnames('flex flex-col', {
            'relative overflow-hidden rounded-[4px] border border-borderColor bg-white dark:bg-[#323232]':
              labelAnimation,
            '!border-primaryRed': labelAnimation && hasErrors,
          })}
        >
          <label
            className={classnames({
              'absolute mt-px w-[99%] pl-[11px] text-lightPrimaryIconText duration-200 peer-focus:left-3 peer-focus:text-tiny dark:bg-[#323232]':
                labelAnimation,
              'text-[11px]': labelAnimation && hasValue,
              'mb-2 flex flex-nowrap items-center pr-6 font-bold text-primaryTextColor': !labelAnimation,
            })}
            htmlFor={id}
          >
            {label}
            {required && (
              <span className="pl-1 text-primaryRed" data-testid={`${label}.required`}>
                *
              </span>
            )}
            {instructions != null && <> {instructions}</>}
          </label>
          <textarea
            {...field}
            {...restProps}
            id={id}
            data-testid={name}
            minLength={isWordMode ? undefined : minLength}
            maxLength={isWordMode ? undefined : maxLength}
            className={classnames(className, {
              'peer mt-[18px] w-full border-none pt-0': labelAnimation,
              'rounded-[4px] border border-[#D9D9D9]': !labelAnimation,
              '!border-primaryRed': !labelAnimation && hasErrors,
            })}
          />
        </div>

        {showCounter && (
          <div className="mt-1 flex justify-end text-xs text-primaryTextColor">
            <span className={isOverLimit ? 'text-primaryRed' : undefined}>
              {currentCount}
              {counterSuffix}
            </span>
          </div>
        )}

        {meta.touched && meta.error != null && <div className="mt-2 text-[13px] text-errorMessages">{meta.error}</div>}

        {errors.map((error, i) => (
          <div key={i} className="mt-2 text-[13px] text-errorMessages">
            {error}
          </div>
        ))}
      </div>
    </div>
  )
}
